import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  BoltIcon,
  ArrowLeftIcon,
  EnvelopeIcon,
  KeyIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { buildApiUrl } from "../config/api";
import toast from "../utils/toast";
import { useTranslation } from "react-i18next";

export const ForgotPassword: React.FC = () => {
  const { t } = useTranslation(["auth", "common", "errors"]);
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<"email" | "verify" | "reset">(
    "email"
  );
  const [loadingAction, setLoadingAction] = useState<
    null | "send" | "verify" | "reset"
  >(null);
  const [email, setEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const loading = loadingAction !== null;

  const sendResetCode = async (nextStep: "email" | "verify" = "verify") => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      toast.error(t("auth:emailRequired"));
      return false;
    }

    try {
      setLoadingAction("send");
      setEmail(trimmedEmail);
      const response = await fetch(buildApiUrl("/auth/forgot-password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        setCurrentStep(nextStep);
        return true;
      }

      if (response.status === 404) {
        toast.error(data.message || t("auth:emailNotFound"));
      } else {
        toast.error(data.message || t("errors:genericError"));
      }
      return false;
    } catch (error) {
      toast.error(t("errors:connectionError"));
      return false;
    } finally {
      setLoadingAction(null);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendResetCode("verify");
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetCode.trim()) {
      toast.error(t("auth:verificationCodeRequired"));
      return;
    }

    try {
      setLoadingAction("verify");
      const response = await fetch(buildApiUrl("/auth/verify-reset-code"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, resetCode: resetCode.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(t("auth:codeVerified"));
        setCurrentStep("reset");
      } else {
        toast.error(data.message || t("auth:invalidCode"));
      }
    } catch (error) {
      toast.error(t("errors:connectionError"));
    } finally {
      setLoadingAction(null);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error(t("errors:allFieldsRequired"));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t("errors:passwordsDoNotMatch"));
      return;
    }

    if (newPassword.length < 6) {
      toast.error(t("errors:minLength", { min: 6 }));
      return;
    }

    try {
      setLoadingAction("reset");
      const response = await fetch(buildApiUrl("/auth/reset-password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          resetCode,
          newPassword: newPassword.trim(),
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        navigate("/login");
      } else {
        toast.error(data.message || t("auth:passwordResetFailed"));
      }
    } catch (error) {
      toast.error(t("errors:connectionError"));
    } finally {
      setLoadingAction(null);
    }
  };

  const renderEmailStep = () => (
    <>
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-6">
          <EnvelopeIcon className="w-16 h-16 text-blue-500" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">
          {t("auth:forgotPasswordTitle")}
        </h2>
        <p className="text-slate-600">{t("auth:forgotPasswordDescription")}</p>
      </div>

      <form className="space-y-6" onSubmit={handleEmailSubmit}>
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-semibold text-slate-700 mb-2"
          >
            {t("auth:email")}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            className="w-full px-4 py-3 bg-white/80 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-lg placeholder-slate-400"
            placeholder="ornek@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="group relative w-full flex items-center justify-center py-4 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {loadingAction === "send" ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              {t("auth:sendingCode")}
            </div>
          ) : (
            <div className="flex items-center">
              <EnvelopeIcon className="w-5 h-5 mr-2" />
              <span>{t("auth:sendCode")}</span>
            </div>
          )}
        </button>
      </form>
    </>
  );

  const renderVerifyStep = () => (
    <>
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-6">
          <ShieldCheckIcon className="w-16 h-16 text-green-500" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">
          {t("auth:verifyCode")}
        </h2>
        <p className="text-slate-600">
          {t("auth:verifyCodeDescription", { email })}
        </p>
      </div>

      <form className="space-y-6" onSubmit={handleVerifyCode}>
        <div>
          <label
            htmlFor="resetCode"
            className="block text-sm font-semibold text-slate-700 mb-2"
          >
            {t("auth:verificationCode")}
          </label>
          <input
            id="resetCode"
            name="resetCode"
            type="text"
            required
            maxLength={6}
            className="w-full px-4 py-3 bg-white/80 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-lg placeholder-slate-400 text-center text-lg font-mono"
            placeholder="123456"
            value={resetCode}
            onChange={(e) =>
              setResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="group relative w-full flex items-center justify-center py-4 px-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {loadingAction === "verify" ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              {t("auth:verifying")}
            </div>
          ) : (
            <div className="flex items-center">
              <ShieldCheckIcon className="w-5 h-5 mr-2" />
              <span>{t("auth:verifyCodeButton")}</span>
            </div>
          )}
        </button>

        <div className="flex items-center justify-between gap-3 text-sm">
          <button
            type="button"
            onClick={() => void sendResetCode("verify")}
            disabled={loading}
            className="text-blue-600 hover:text-blue-500 font-medium transition-colors disabled:text-slate-400 disabled:cursor-not-allowed"
          >
            {loadingAction === "send"
              ? t("auth:resendingCode")
              : t("auth:resendCode")}
          </button>
          <button
            type="button"
            onClick={() => setCurrentStep("email")}
            className="text-blue-600 hover:text-blue-500 font-medium transition-colors"
          >
            ← {t("auth:changeEmail")}
          </button>
        </div>
      </form>
    </>
  );

  const renderResetStep = () => (
    <>
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-6">
          <KeyIcon className="w-16 h-16 text-purple-500" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">
          {t("auth:newPasswordTitle")}
        </h2>
        <p className="text-slate-600">{t("auth:newPasswordDescription")}</p>
      </div>

      <form className="space-y-6" onSubmit={handleResetPassword}>
        <div>
          <label
            htmlFor="newPassword"
            className="block text-sm font-semibold text-slate-700 mb-2"
          >
            {t("auth:newPassword")}
          </label>
          <input
            id="newPassword"
            name="newPassword"
            type="password"
            required
            className="w-full px-4 py-3 bg-white/80 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-lg placeholder-slate-400"
            placeholder="••••••••"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </div>

        <div>
          <label
            htmlFor="confirmPassword"
            className="block text-sm font-semibold text-slate-700 mb-2"
          >
            {t("auth:confirmPassword")}
          </label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            required
            className="w-full px-4 py-3 bg-white/80 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm focus:shadow-lg placeholder-slate-400"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="group relative w-full flex items-center justify-center py-4 px-6 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {loadingAction === "reset" ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              {t("auth:updatingPassword")}
            </div>
          ) : (
            <div className="flex items-center">
              <KeyIcon className="w-5 h-5 mr-2" />
              <span>{t("auth:updatePassword")}</span>
            </div>
          )}
        </button>
      </form>
    </>
  );

  return (
    <div
      className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center py-6 px-4 sm:px-6 lg:px-8"
      style={{ minHeight: "calc(100vh / var(--app-ui-scale, 1))" }}
    >
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-400/20 to-pink-500/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-emerald-400/20 to-blue-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-md w-full space-y-8">
        {/* Reset Card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8">
          {/* Header with Logo */}
          <div className="text-center mb-8">
            <Link
              to="/login"
              className="inline-flex items-center text-blue-600 hover:text-blue-500 mb-6"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              {t("auth:backToLogin")}
            </Link>
            <div className="flex items-center justify-center mb-6">
              <BoltIcon className="w-12 h-12 text-blue-500 mr-2" />
              <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                FlowMind
              </h1>
            </div>
          </div>

          {/* Step Content */}
          {currentStep === "email" && renderEmailStep()}
          {currentStep === "verify" && renderVerifyStep()}
          {currentStep === "reset" && renderResetStep()}
        </div>

        {/* Footer Info */}
        <div className="text-center">
          <p className="text-sm text-slate-500">
            {t("auth:copyright", { year: currentYear })}
          </p>
        </div>
      </div>
    </div>
  );
};


